import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * 统一的认证检查Hook
 * 用于在需要登录的操作中统一处理登录逻辑
 */
export const useAuthCheck = () => {
  const { user, isAuthenticated, loading, isInitialized } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const pendingCallbackRef = useRef(null);
  const navigate = useNavigate();

  /**
   * 检查用户是否已登录，如果未登录则显示登录弹窗
   * @param {Function} callback - 登录成功后执行的回调函数
   * @returns {boolean} - 如果已登录返回true，未登录返回false
   */
  const checkAuthAndExecute = (callback) => {
    // 如果认证状态还在初始化中，等待初始化完成
    if (!isInitialized || loading) {
      return false;
    }

    if (isAuthenticated && user) {
      // 已登录，直接执行回调
      if (callback) {
        callback();
      }
      return true;
    } else {
      // 未登录，保存回调函数并显示登录弹窗
      pendingCallbackRef.current = callback;
      setShowLoginModal(true);
      return false;
    }
  };

  /**
   * 检查用户是否已登录，如果未登录则跳转到登录页面（用于登录过期场景）
   * @param {Function} callback - 登录成功后执行的回调函数
   * @returns {boolean} - 如果已登录返回true，未登录返回false
   */
  const checkAuthAndRedirect = (callback) => {
    // 如果认证状态还在初始化中，等待初始化完成
    if (!isInitialized || loading) {
      return false;
    }

    if (isAuthenticated && user) {
      // 已登录，直接执行回调
      if (callback) {
        callback();
      }
      return true;
    } else {
      // 检查是否有token但用户为空（登录过期场景）
      const hasToken = localStorage.getItem('token');
      if (hasToken) {
        // 有token但用户为空，说明是登录过期，直接跳转
        navigate('/login');
      } else {
        // 没有token，说明是未登录，显示弹窗
        pendingCallbackRef.current = callback;
        setShowLoginModal(true);
      }
      return false;
    }
  };

  /**
   * 登录成功后的处理函数
   */
  const handleLoginSuccess = (result) => {
    setShowLoginModal(false);
    // 执行之前保存的回调函数
    if (pendingCallbackRef.current) {
      const callback = pendingCallbackRef.current;
      pendingCallbackRef.current = null; // 清除回调
      callback();
    }
  };

  /**
   * 关闭登录弹窗
   */
  const handleCloseLogin = () => {
    setShowLoginModal(false);
    // 清除待执行的回调
    pendingCallbackRef.current = null;
  };

  return {
    user,
    showLoginModal,
    checkAuthAndExecute,
    checkAuthAndRedirect,
    handleLoginSuccess,
    handleCloseLogin
  };
};